import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { blogPosts, allCategories, allTags } from "../data/blogPosts";
import PostCard from "../components/PostCard";
import { Link, useSearchParams } from "react-router-dom";

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const qParam = params.get("q") || "";
  const catParam = params.get("cat") || "All";
  const tagParam = params.get("tag") || "";
  const pageParam = parseInt(params.get("page") || "1", 10);

  const pageSize = 6;

  const onFilterChange = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "All") next.delete(key);
    else next.set(key, value);
    next.delete("page"); // reset to page 1 on filter change
    setParams(next);
  };

  const filtered = blogPosts.filter((p) => {
    const qMatch =
      !qParam ||
      p.title.toLowerCase().includes(qParam.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(qParam.toLowerCase()) ||
      p.content.toLowerCase().includes(qParam.toLowerCase());
    const catMatch = catParam === "All" || p.category === catParam;
    const tagMatch = !tagParam || p.tags.includes(tagParam);
    return qMatch && catMatch && tagMatch;
  });

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, pageParam), totalPages);

  const start = (page - 1) * pageSize;
  const visible = filtered.slice(start, start + pageSize);

  const featured = blogPosts[0];

  const setPage = (n) => {
    const next = new URLSearchParams(params);
    next.set("page", String(n));
    setParams(next);
  };

  return (
    <>
      <Helmet>
        <title>Blog | Your Choice Associates</title>
        <meta
          name="description"
          content="Learn about Medicare, Part D, Medigap, and life insurance. Practical guides and plain‑English tips from Your Choice Associates."
        />
        <meta property="og:title" content="Blog | Your Choice Associates" />
        <meta
          property="og:description"
          content="Practical guides on Medicare & life insurance to help you choose with confidence."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      {/* Hero / Featured */}
      <section className="bg-[#f5f8fc]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block text-xs font-semibold tracking-wider uppercase text-[#002B5C] bg-[#e8f1fb] px-3 py-1 rounded-full">
              Our Blog
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-[#002B5C]">
              Practical Medicare & Life Insurance Guides
            </h1>
            <p className="mt-3 text-gray-600">
              Clear, unbiased explanations so you can choose with confidence.
              Updated regularly by Your Choice Associates.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm text-gray-500">
              <span>Featured:</span>
              <Link
                className="text-[#002B5C] font-semibold hover:underline"
                to={`/blog/${featured.slug}`}
              >
                {featured.title}
              </Link>
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <img
              src={featured.cover.src}
              alt={featured.cover.alt}
              width="1600"
              height="900"
              className="w-full h-72 object-cover"
              loading="lazy"
            />
            <figcaption className="px-4 py-2 text-xs text-gray-500 bg-white">
              Photo: {featured.cover.credit}
            </figcaption>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 pt-10">
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex-1">
            <label className="sr-only" htmlFor="blog-search">Search</label>
            <input
              id="blog-search"
              type="text"
              value={qParam}
              onChange={(e) => onFilterChange("q", e.target.value)}
              placeholder="Search topics, e.g. “Part D”, “Medigap”..."
              className="w-full md:w-[440px] rounded-lg border border-gray-200 bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#8cb3e6]"
            />
          </div>

          <div className="flex gap-3">
            <select
              value={catParam}
              onChange={(e) => onFilterChange("cat", e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#8cb3e6]"
            >
              {allCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={tagParam}
              onChange={(e) => onFilterChange("tag", e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#8cb3e6]"
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tag pills quick filter */}
        <div className="mt-4 flex flex-wrap gap-2">
          {allTags.slice(0, 10).map((t) => {
            const active = t === tagParam;
            return (
              <button
                key={t}
                onClick={() => onFilterChange("tag", active ? "" : t)}
                className={`text-xs px-3 py-1 rounded-full border ${
                  active
                    ? "bg-[#002B5C] text-white border-[#002B5C]"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                #{t}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        {visible.length === 0 ? (
          <p className="text-gray-600">No posts found. Try a different search.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {visible.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`px-3 py-2 rounded-lg border ${
                  n === page
                    ? "bg-[#002B5C] text-white border-[#002B5C]"
                    : "bg-white border-gray-200"
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </section>

      <Footer />
    </>
  );
}
